<script setup name="TvbRecommendVideos">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import TvbPlayIcon from '@/components/TvbPlayIcon/index.vue'

const router = useRouter()

const props = defineProps({
  /**
   * 推荐视频列表
   * @type {Array<{cover: string, title: string, url: string}>}
   */
  featuredVideos: {
    type: Array,
    default: () => []
  },
  /**
   * 是否显示推荐区域
   */
  visible: {
    type: Boolean,
    default: false
  },
  /**
   * 是否为移动端
   */
  isMobile: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['replay', 'next', 'navigate'])

// 倒计时相关
const COUNTDOWN_SECONDS = 10
const countdown = ref(COUNTDOWN_SECONDS)
const countdownTimer = ref(null)
const isPaused = ref(false)

// 轮播图相关
const carouselRef = ref(null)
const currentPage = ref(0)

// 容器宽度监听相关
const containerRef = ref(null)
const containerWidth = ref(0)
let resizeObserver = null

// 响应式布局常量
const MIN_CARD_WIDTH = 200
const SPACE_BETWEEN = 20

// 计算每页显示的卡片数（PC端响应式）
const slidesPerView = computed(() => {
  if (props.isMobile) return 'auto'

  // 容器宽度为0时使用默认值
  if (containerWidth.value === 0) return 4

  const width = containerWidth.value - 60

  // 根据容器宽度计算显示数量
  let count = 4
  if (width >= 800) {
    count = 4
  } else if (width >= 600) {
    count = 3
  } else if (width >= 400) {
    count = 2
  } else {
    count = 1
  }

  // 确保结果在1-4之间
  return Math.max(1, Math.min(4, count))
})

// 是否显示箭头（基于视频总数和slidesPerView计算）
const showArrows = computed(() => {
  if (props.isMobile) return false
  return props.featuredVideos.length > slidesPerView.value
})

// 计算最大页数
const maxPage = computed(() => {
  if (props.isMobile || slidesPerView.value === 'auto') return 0
  const perView = typeof slidesPerView.value === 'number' ? slidesPerView.value : 4
  return Math.max(0, props.featuredVideos.length - perView)
})

// 是否显示上一页箭头（不在第一页时显示）
const showPrevArrow = computed(() => {
  return currentPage.value > 0
})

// 是否显示下一页箭头（不在最后一页时显示）
const showNextArrow = computed(() => {
  return currentPage.value < maxPage.value
})

// 处理上一页
const handlePrev = () => {
  if (carouselRef.value && currentPage.value > 0) {
    carouselRef.value.prev()
  }
}

// 处理下一页
const handleNext = () => {
  if (carouselRef.value && currentPage.value < maxPage.value) {
    carouselRef.value.next()
  }
}

// 开始倒计时
const startCountdown = () => {
  if (countdownTimer.value) return

  countdownTimer.value = setInterval(() => {
    if (!isPaused.value && countdown.value > 0) {
      countdown.value--
    }

    if (countdown.value <= 0) {
      clearInterval(countdownTimer.value)
      countdownTimer.value = null
      emits('next')
    }
  }, 1000)
}

// 停止倒计时
const stopCountdown = () => {
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value)
    countdownTimer.value = null
  }
}

// 重置倒计时
const resetCountdown = () => {
  stopCountdown()
  countdown.value = COUNTDOWN_SECONDS
}

// 处理重播按钮点击
const handleReplay = () => {
  resetCountdown()
  emits('replay')
}

// 处理视频卡片点击
const handleVideoClick = (video) => {
  stopCountdown()
  emits('navigate', video)
}

// 处理轮播图切换
const handleCarouselChange = (index) => {
  currentPage.value = index
}

// 监听 visible 变化
watch(
  () => props.visible,
  (newVal) => {
    if (newVal && props.featuredVideos.length > 0) {
      resetCountdown()
      startCountdown()
    } else {
      stopCountdown()
    }
  },
  { immediate: true }
)

// 初始化 ResizeObserver
onMounted(() => {
  if (containerRef.value && !props.isMobile) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        containerWidth.value = entry.contentRect.width
      }
    })
    resizeObserver.observe(containerRef.value)
  }
})

// 组件卸载时清理
onUnmounted(() => {
  stopCountdown()
  // 清理 ResizeObserver
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})

// 计算倒计时圆环的 stroke-dashoffset
const circleCircumference = 2 * Math.PI * 10 // r=10
const strokeDashoffset = computed(() => {
  const progress = countdown.value / COUNTDOWN_SECONDS
  return circleCircumference * (1 - progress)
})
</script>

<template>
  <div
    v-show="visible && featuredVideos.length > 0"
    class="recommend-videos"
    :class="{ 'is-mobile': isMobile }"
  >
    <!-- 顶部区域：倒计时提示 + 重播按钮 -->
    <div class="recommend-header">
      <div class="countdown-info">
        <!-- 倒计时圆环 -->
        <div class="countdown-circle">
          <svg :viewBox="isMobile ? '0 0 20 20' : '0 0 24 24'" class="countdown-svg">
            <!-- 背景圆环 -->
            <circle
              :cx="isMobile ? 10 : 12"
              :cy="isMobile ? 10 : 12"
              :r="isMobile ? 8 : 10"
              fill="none"
              stroke="rgba(255, 255, 255, 0.2)"
              stroke-width="2"
            />
            <!-- 进度圆环 -->
            <circle
              :cx="isMobile ? 10 : 12"
              :cy="isMobile ? 10 : 12"
              :r="isMobile ? 8 : 10"
              fill="none"
              stroke="white"
              stroke-width="2"
              :stroke-dasharray="isMobile ? 2 * Math.PI * 8 : circleCircumference"
              :stroke-dashoffset="
                isMobile ? 2 * Math.PI * 8 * (1 - countdown / COUNTDOWN_SECONDS) : strokeDashoffset
              "
              :transform="isMobile ? 'rotate(-90 10 10)' : 'rotate(-90 12 12)'"
              class="progress-ring"
            />
          </svg>
          <span class="countdown-number">{{ countdown }}</span>
        </div>
        <!-- 提示文字 -->
        <span class="countdown-text">秒後自動播放下一則影片</span>
      </div>

      <!-- 重播按钮 -->
      <button class="replay-button" @click="handleReplay">
        <svg class="replay-icon" viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"
          />
        </svg>
        <span class="replay-text">{{ isMobile ? '重播' : '重播當前影片' }}</span>
      </button>
    </div>

    <!-- 视频列表区域 -->
    <div ref="containerRef" class="recommend-list">
      <!-- 自定义上一页箭头 -->
      <button
        v-if="showArrows && showPrevArrow"
        class="carousel-arrow carousel-arrow-prev"
        @click="handlePrev"
        aria-label="上一页"
      >
        <svg
          width="9"
          height="16"
          viewBox="0 0 9 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.20504 0.205025C8.47841 0.478392 8.47841 0.921608 8.20504 1.19497L2.24655 7.15346L8.20504 13.112C8.47841 13.3853 8.47841 13.8286 8.20504 14.1019C7.93168 14.3753 7.48846 14.3753 7.21509 14.1019L0.794962 7.68184C0.521595 7.40847 0.521595 6.96526 0.794962 6.69189L7.21509 0.205025C7.48846 -0.0683418 7.93168 -0.0683418 8.20504 0.205025Z"
            fill="white"
            fill-opacity="0.9"
          />
        </svg>
      </button>

      <n-carousel
        ref="carouselRef"
        :slides-per-view="slidesPerView"
        :space-between="isMobile ? 10 : SPACE_BETWEEN"
        :loop="false"
        :show-arrow="false"
        :show-dots="false"
        :touchable="true"
        :draggable="!isMobile"
        @update:current-index="handleCarouselChange"
        :key="slidesPerView"
      >
        <div
          v-for="(video, index) in featuredVideos"
          :key="index"
          class="video-card"
          @click="handleVideoClick(video)"
        >
          <!-- 视频封面 -->
          <div class="video-cover">
            <img :src="video.cover" :alt="video.title" class="cover-image" />
            <!-- 播放图标 -->
            <div class="play-overlay">
              <TvbPlayIcon :size="isMobile ? 32 : 48" class="play-icon" />
            </div>
          </div>
          <!-- 视频标题 -->
          <p class="video-title">{{ video.title }}</p>
        </div>
      </n-carousel>

      <!-- 自定义下一页箭头 -->
      <button
        v-if="showArrows && showNextArrow"
        class="carousel-arrow carousel-arrow-next"
        @click="handleNext"
        aria-label="下一页"
      >
        <svg
          width="9"
          height="16"
          viewBox="0 0 9 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.79496 0.205025C0.521593 0.478392 0.521593 0.921608 0.79496 1.19497L6.75345 7.15346L0.79496 13.112C0.521593 13.3853 0.521593 13.8286 0.79496 14.1019C1.06833 14.3753 1.51154 14.3753 1.78491 14.1019L8.20504 7.68184C8.47841 7.40847 8.47841 6.96526 8.20504 6.69189L1.78491 0.205025C1.51154 -0.0683418 1.06833 -0.0683418 0.79496 0.205025Z"
            fill="white"
            fill-opacity="0.9"
          />
        </svg>
      </button>
    </div>

    <!-- 移动端轮播指示器
    <div v-if="isMobile && featuredVideos.length > 1" class="carousel-indicators">
      <span
        v-for="(_, index) in featuredVideos"
        :key="index"
        class="indicator"
        :class="{ active: currentPage === index }"
      />
    </div> -->
  </div>
</template>

<style lang="scss" scoped>
.recommend-videos {
  // PC端样式 - 位于播放器下方
  background: rgba(0, 0, 0, 0.7);
  border-radius: 24px;
  padding: 24px 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  // 移动端样式 - 覆盖整个播放器
  &.is-mobile {
    position: absolute;
    inset: 0;
    border-radius: 0;
    padding: 16px;
    justify-content: center;
    z-index: 100;
    gap: 0;
  }
}

// 顶部区域
.recommend-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .is-mobile & {
    margin-bottom: 14px;
  }
}

// 倒计时信息
.countdown-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

// 倒计时圆环
.countdown-circle {
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  .is-mobile & {
    width: 20px;
    height: 20px;
  }
}

.countdown-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.progress-ring {
  transition: stroke-dashoffset 1s linear;
}

.countdown-number {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  line-height: 20px;
  letter-spacing: 0.3px;
  z-index: 1;

  .is-mobile & {
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
  }
}

// 倒计时提示文字
.countdown-text {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  line-height: 26px;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .is-mobile & {
    font-size: 16px;
    line-height: 24px;
  }
}

// 重播按钮
.replay-button {
  display: flex;
  align-items: center;
  gap: 3px;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  border-radius: 12px;
  padding: 7px 12px 7px 14px;
  cursor: pointer;
  transition: all 0.15s linear;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  .is-mobile & {
    padding: 6px 12px;
  }
}

.replay-icon {
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.9);
  flex-shrink: 0;

  .is-mobile & {
    width: 14px;
    height: 14px;
  }
}

.replay-text {
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  line-height: 22px;
  letter-spacing: 0.3px;
  white-space: nowrap;

  .is-mobile & {
    font-size: 14px;
    line-height: 20px;
  }
}

// 视频列表
.recommend-list {
  width: 100%;

  :deep(.n-carousel) {
    width: 100%;
  }

  :deep(.n-carousel__slide) {
    display: flex;
  }

  // 移动端 carousel slide 固定宽度
  .is-mobile & {
    :deep(.n-carousel) {
      overflow: unset;
    }
    :deep(.n-carousel__slide) {
      width: 160px !important;
      min-width: 160px !important;
      max-width: 160px !important;
      flex: 0 0 160px !important;
    }
  }
}

// 视频卡片
.video-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  cursor: pointer;
  min-width: 200px;
  flex: 1;

  .is-mobile & {
    min-width: 160px !important;
    width: 160px !important;
    max-width: 160px !important;
    flex: 0 0 160px !important;
    gap: 6px;
  }
}

// 视频封面
.video-cover {
  position: relative;
  aspect-ratio: 303 / 170.6;
  border-radius: 16px;
  overflow: hidden;
  border: 0.5px solid rgba(163, 178, 188, 0.35);

  .is-mobile & {
    border-radius: 12px;
  }
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  .video-card:hover & {
    transform: scale(1.05);
  }
}

// 播放图标遮罩
.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  opacity: 0.8;
  transition: opacity 0.3s ease;

  .video-card:hover & {
    opacity: 1;
  }
}

.play-icon {
  color: white;
}

// 视频标题
.video-title {
  font-size: 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  line-height: 24px;
  letter-spacing: 0.5px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;

  .is-mobile & {
    font-weight: 400;
    white-space: nowrap;
  }
}

// PC端视频标题显示2行并带省略号
.recommend-videos:not(.is-mobile) .video-title {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
}

// 移动端轮播指示器
.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
}

.indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transition: background 0.3s ease;

  &.active {
    background: rgba(255, 255, 255, 0.9);
  }
}

// 自定义轮播箭头样式（PC端）
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #333;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
  transition: all 0.15s linear;
  z-index: 10;

  &:hover {
    background: #444;
    transform: translateY(-50%) scale(1.05);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    width: 9px;
    height: 16px;
    flex-shrink: 0;
  }
}

// 上一页箭头（左侧）
.carousel-arrow-prev {
  left: 11px;
}

// 下一页箭头（右侧）
.carousel-arrow-next {
  right: 11px;
}
</style>
